import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fetchDossiers } from '../api';
import { Sentences } from '../lib/text';
import {
  ShieldAlert, FileText, ExternalLink, Download, Loader2, Archive,
  Eye, Filter, Calendar, MapPin, Search, Flame, Gavel, ArrowRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FAUCI_BREAKING = {
  id: 'fauci-5eme-amendement-2026-07-29',
  date: '29 juillet 2026',
  title: "L'AVEU DE FAUCI : L'Invocation du 5ème Amendement",
  badge: 'AUDITION SÉNAT — HISTORIQUE',
  image: 'https://static.prod-images.emergentagent.com/jobs/cd77cba8-9d76-4084-b22c-c43c48341fc2/images/a24159efbe0055f38e2639d70340abee17da627c72ae899ec3c546aa771fd125.jpeg',
  href: 'https://ah2020.fr/actualites/fauci-aveu-silence-5eme-amendement-juillet-2026',
};

function FauciBreakingCard() {
  return (
    <motion.article
      data-testid="breaking-fauci-card"
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="breaking-card"
    >
      <div className="breaking-card__stripe" />

      {/* Cover image */}
      <div className="relative aspect-[21/9] w-full overflow-hidden bg-black">
        <img
          src={FAUCI_BREAKING.image}
          alt={FAUCI_BREAKING.title}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(0.72) contrast(1.05)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(20,8,8,0.55) 65%, rgba(20,8,8,0.92) 100%)',
          }}
        />
        <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
          <span className="breaking-badge" data-testid="breaking-badge-nouveau">
            <Flame className="h-3 w-3" /> NOUVEAU · BREAKING
          </span>
          <span className="breaking-badge breaking-badge--gold">
            <Gavel className="h-3 w-3" /> {FAUCI_BREAKING.badge}
          </span>
        </div>
        <div className="absolute bottom-4 right-4 mono text-[10px] tracking-[0.28em] text-white/80 uppercase bg-black/50 px-2 py-1 rounded border border-white/10">
          {FAUCI_BREAKING.date}
        </div>
        <div className="absolute bottom-5 left-5 right-5 pr-24">
          <h2 className="serif text-2xl md:text-4xl lg:text-5xl leading-[1.05] text-1 drop-shadow-2xl">
            <span className="text-rose-300">L'AVEU DE FAUCI</span>
            <span className="block text-1 text-xl md:text-2xl lg:text-3xl italic font-light mt-1">
              L'Invocation du 5ème Amendement
            </span>
          </h2>
        </div>
      </div>

      <div className="breaking-card__body">
        <p className="text-2 text-[15px] leading-relaxed">
          Le <strong className="text-rose-300">29 juillet 2026</strong>, le Dr Anthony Fauci, ancien
          directeur du NIAID et figure centrale de la gestion pandémique mondiale, a comparu devant la
          Commission sénatoriale de la Sécurité Intérieure et des Affaires Gouvernementales, convoqué
          par <em>subpoena</em> du Sénateur Rand Paul.
        </p>

        <div className="mt-5">
          <div className="mono text-[10px] tracking-[0.28em] text-rose-300 uppercase mb-2 inline-flex items-center gap-2">
            <Flame className="h-3 w-3" /> Les faits
          </div>
          <ul>
            <li>
              <strong className="text-amber-300">1 141 pages de journaux intimes</strong> — Le Sénateur
              Paul a rendu publics les journaux privés de Fauci, révélant ses pensées réelles sur les
              origines du COVID-19, ses réflexions sur sa propre célébrité, et des contradictions
              flagrantes avec ses déclarations publiques sous serment.
            </li>
            <li>
              <strong className="text-amber-300">Invocation du 5ème Amendement</strong> — Face aux
              questions du Sénat, Fauci a REFUSÉ de répondre en invoquant le 5ème Amendement de la
              Constitution américaine (droit de ne pas s'auto-incriminer). Il a déclaré agir « sur les
              conseils de son avocat » et que cette décision le « peinait ».
            </li>
            <li>
              <strong className="text-amber-300">L'aveu par le silence</strong> — En invoquant le 5ème
              Amendement, Fauci reconnaît implicitement que ses réponses pourraient l'INCRIMINER. Un
              innocent n'a pas besoin de se protéger contre l'auto-incrimination.
            </li>
            <li>
              <strong className="text-amber-300">Les journaux contredisent les témoignages publics</strong>
              {' '}— Les 1 141 pages révèlent que Fauci était conscient, en privé, de faits qu'il a niés
              publiquement concernant les origines du virus, le financement de la recherche
              gain-of-function, et ses liens avec le laboratoire de Wuhan.
            </li>
          </ul>
        </div>

        <div className="strategic-note mt-6">
          <div className="strategic-note__header">
            <Flame className="h-3.5 w-3.5" /> Signification stratégique
          </div>
          <div className="strategic-note__body">
            Cette audition marque un tournant historique : l'homme qui a orchestré la réponse pandémique
            mondiale, imposé les confinements, promu les vaccins expérimentaux et nié les origines
            artificielles du virus REFUSE désormais de parler sous serment. Le silence de Fauci est son
            aveu le plus éloquent. <strong className="text-amber-300">La justice arrive.</strong>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 pt-4 border-t border-rose-500/20">
          <a
            href={FAUCI_BREAKING.href}
            target="_blank"
            rel="noreferrer"
            data-testid="breaking-fauci-dossier-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all"
            style={{
              background: 'linear-gradient(180deg, rgba(220,38,38,0.28), rgba(220,38,38,0.14))',
              border: '1px solid rgba(220,38,38,0.6)',
              color: '#fee2e2',
              letterSpacing: '0.08em',
            }}
          >
            DOSSIER COMPLET <ArrowRight className="h-4 w-4" />
          </a>
          <a
            href={FAUCI_BREAKING.href}
            target="_blank"
            rel="noreferrer"
            className="mono text-[11px] text-3 hover:text-rose-200 inline-flex items-center gap-1 truncate max-w-full"
          >
            <ExternalLink className="h-3 w-3" /> ah2020.fr/actualites/fauci-aveu-silence-5eme-amendement-juillet-2026
          </a>
          <span className="ml-auto mono text-[10px] tracking-[0.25em] text-4 uppercase">
            ARCHIVES SOUVERAINES · DOSSIER {FAUCI_BREAKING.id.toUpperCase()}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

export default function CIAPage() {
  const [docs, setDocs] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [{ items }, catRes] = await Promise.all([
          fetchDossiers({ agency: 'CIA', limit: 200 }),
          axios.get(`${BACKEND_URL}/api/cia/categories`),
        ]);
        if (cancel) return;
        setDocs(items || []);
        setCats(catRes.data?.categories || []);
      } catch (e) {
        try {
          await axios.post(`${BACKEND_URL}/api/cia/seed`);
          const [{ items }, catRes] = await Promise.all([
            fetchDossiers({ agency: 'CIA', limit: 200 }),
            axios.get(`${BACKEND_URL}/api/cia/categories`),
          ]);
          if (!cancel) {
            setDocs(items || []);
            setCats(catRes.data?.categories || []);
          }
        } catch (e2) { console.error(e2); }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const filtered = useMemo(() => {
    let f = docs;
    if (activeCat !== 'ALL') f = f.filter(d => d.cia_category === activeCat);
    if (search.trim()) {
      const q = search.toLowerCase();
      f = f.filter(d =>
        d.title.toLowerCase().includes(q) ||
        d.summary.toLowerCase().includes(q) ||
        d.location.toLowerCase().includes(q)
      );
    }
    return f;
  }, [docs, activeCat, search]);

  const totalSize = useMemo(
    () => docs.reduce((s, d) => s + (d.size_mb || 0), 0).toFixed(1),
    [docs],
  );

  return (
    <div data-testid="cia-page">
      {/* HERO classified-style */}
      <section className="relative border-b border-rose-500/15 overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(circle at 20% 20%, rgba(220,38,38,0.10), transparent 50%), radial-gradient(circle at 80% 80%, rgba(245,158,11,0.06), transparent 45%)',
        }} />
        <div className="relative mx-auto max-w-[1400px] px-6 py-14">
          <div className="flex items-start justify-between flex-wrap gap-6">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mono text-[11px] tracking-[0.3em] text-rose-300/90 uppercase mb-4">
                <span className="cia-stamp"><ShieldAlert className="h-3 w-3" /> Central Intelligence Agency</span>
                <span className="text-rose-500/40">::</span>
                <span>Electronic Reading Room · FOIA</span>
              </div>
              <h1 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                <span className="text-1">Déclassifications</span>{' '}
                <span className="italic text-rose-300">CIA</span>
              </h1>
              <div className="text-2 text-base mt-4 max-w-2xl leading-relaxed">
                <Sentences text="Le programme « UFOs: Fact or Fiction? » regroupe les documents internes de la CIA sur les OVNI/UAP, de la première vague de 1947 jusqu'aux années 1990. Mémorandums secrets, panels scientifiques, analyses photographiques du NPIC, et surveillance discrète du phénomène pendant la Guerre Froide. Tous les documents ci-dessous proviennent du CIA Electronic Reading Room et sont accessibles à tous." />
              </div>
              <p className="text-3 text-sm mt-3 mono">
                Cliquez sur un document pour ouvrir le dossier complet — PDF original (anglais) + génération de la traduction française intégrale.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, rotate: -8, scale: 0.85 }}
              animate={{ opacity: 1, rotate: -8, scale: 1 }}
              transition={{ duration: 0.7 }}
              className="declass-stamp"
            >
              <div className="ds-line ds-line-1">DECLASSIFIED</div>
              <div className="ds-line ds-line-2">PURSUE FR · FOIA</div>
              <div className="ds-line ds-line-3">EDITION FRANÇAISE</div>
            </motion.div>
          </div>

          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { lbl: 'Documents', val: docs.length || '—', icon: FileText },
              { lbl: 'Catégories', val: cats.length || '—', icon: Archive },
              { lbl: 'Pages totales', val: docs.length ? '≈ 3 200' : '—', icon: Eye },
              { lbl: 'Volume', val: docs.length ? `${totalSize} Mo` : '—', icon: Download },
            ].map((s, i) => (
              <div key={i} className="glass rounded-md p-3 border-l-2 border-rose-500/60">
                <div className="mono text-[10px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5">
                  <s.icon className="h-3 w-3 text-rose-300/80" /> {s.lbl}
                </div>
                <div className="serif text-2xl text-1 mt-1">{s.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/5 bg-black/30">
        <div className="mx-auto max-w-[1400px] px-6 pt-8 pb-6">
          <div className="mb-4 flex items-center gap-3">
            <Flame className="h-4 w-4 text-rose-400" />
            <div className="mono text-[10px] tracking-[0.3em] text-rose-300 uppercase">
              À la une · Dernière déclassification
            </div>
          </div>
          <FauciBreakingCard />
        </div>
      </section>

      <section className="border-b border-white/5 bg-black/30">
        <div className="mx-auto max-w-[1400px] px-6 py-5">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="mono text-[10px] tracking-widest text-3 uppercase inline-flex items-center gap-1.5">
              <Filter className="h-3 w-3" /> Sous-collections
            </div>
            <button
              data-testid="cia-filter-all"
              onClick={() => setActiveCat('ALL')}
              className={`mono text-[11px] px-2.5 py-1 rounded border ${activeCat === 'ALL' ? 'bg-rose-400/15 border-rose-300/40 text-rose-100' : 'border-white/10 text-2 hover:bg-white/5'}`}
            >
              Toutes ({docs.length})
            </button>
            {cats.map(c => (
              <button
                key={c.id}
                data-testid={`cia-filter-${c.id}`}
                onClick={() => setActiveCat(c.id)}
                className={`mono text-[11px] px-2.5 py-1 rounded border inline-flex items-center gap-1.5 ${activeCat === c.id ? 'border-rose-300/40 text-rose-100 bg-rose-400/10' : 'border-white/10 text-2 hover:bg-white/5'}`}
                style={activeCat === c.id ? { borderLeftColor: c.color, borderLeftWidth: 3 } : {}}
              >
                <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ background: c.color }} />
                {c.label} ({c.count})
              </button>
            ))}

            <div className="ml-auto relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-3" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher…"
                data-testid="cia-search"
                className="pl-7 pr-3 py-1.5 mono text-[11px] rounded border border-white/10 bg-black/40 focus:outline-none focus:border-rose-300/40 w-48"
              />
            </div>
          </div>

          {activeCat !== 'ALL' && (
            <p className="mt-3 text-2 text-sm max-w-3xl">
              {cats.find(c => c.id === activeCat)?.desc}
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="mx-auto max-w-[1400px] px-6 py-10">
          {loading ? (
            <div className="flex items-center gap-2 text-3 mono text-sm">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement de l'archive CIA…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-3 mono text-sm">Aucun document pour ce filtre.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((d, i) => {
                const cat = cats.find(c => c.id === d.cia_category);
                return (
                  <motion.div
                    key={d.id}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.4 }}
                  >
                    <Link
                      to={`/dossier/${d.id}`}
                      data-testid={`cia-doc-${d.id}`}
                      className="cia-card block rounded-lg overflow-hidden group"
                    >
                      <div className="cia-card-header relative px-4 py-3 border-b border-rose-500/15">
                        <div className="flex items-center justify-between">
                          <span className="mono text-[9px] tracking-widest uppercase inline-flex items-center gap-1.5"
                                style={{ color: cat?.color }}>
                            <span className="h-1.5 w-1.5 rounded-full inline-block" style={{ background: cat?.color }} />
                            {cat?.label || 'CIA'}
                          </span>
                          <span className="mono text-[9px] text-3">{d.id}</span>
                        </div>
                        <div className="mono text-[9px] text-3 mt-1 flex items-center gap-2">
                          <Calendar className="h-3 w-3" /> {d.date || '—'}
                          <span className="text-4">·</span>
                          <Download className="h-3 w-3" /> {d.size_mb ? `${d.size_mb} Mo` : '?'}
                        </div>
                      </div>
                      <div className="p-4">
                        <div className="text-1 text-sm font-medium leading-snug mb-2 line-clamp-2 group-hover:text-rose-200 transition-colors">
                          {d.title}
                        </div>
                        <p className="text-3 text-xs line-clamp-3 leading-relaxed">{d.summary}</p>
                        <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
                          <span className="mono text-[9px] text-3 inline-flex items-center gap-1 truncate max-w-[60%]">
                            <MapPin className="h-3 w-3" /> {d.location || 'N/A'}
                          </span>
                          <span className="text-rose-300 text-[11px] inline-flex items-center gap-1">
                            Ouvrir le dossier <ExternalLink className="h-3 w-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
